import { useEffect, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { useRecoilState } from "recoil";
import { UserSearch } from "@/models/user";
import { Button, Input, message, Modal, Tabs, Table, Pagination, Tag } from "antd";
import { useMutation } from "react-query";
import { getUserList, getInviteRelationsList, getCommissionRecordsList } from "@/services/user";
import { getOrderList, getPrintBookRecordList, getPreLockList } from "@/services/order";
import _ from "lodash";
import { RegisterChannel, UserStatus, BindType, CommissionStatus, InviteLevel, GradeConfig } from "@/constants/common";
import dayjs from "dayjs";
import { OrderStatus, OrderType, PreLockStatus, PrintStatus, PrintType } from "@/constants/order";
import { useRightsDict } from "@/hooks/user";

export default function CustomUserSearch() {
  // 用户搜索
  const [userSearch, setUserSearch] = useRecoilState(UserSearch);
  // 每页条数
  const [pageSize] = useState(20);
  // 权益字典
  const { rightsDict } = useRightsDict();

  // 处理搜索
  const handleSearch = useMutation(async (activeKey: string) => {
    // 解构
    const { user_id, mobile } = userSearch;
    setUserSearch({ ...userSearch, data: null, page: 1 });
    // 搜索
    switch (activeKey) {
      case "1":
        !user_id && !mobile ? message.info("请输入用户Id或手机号") : getUserData.mutate({ activeKey, page: 1 });
        break;
      case "2":
        !user_id ? message.info("请输入用户Id") : getOrderData.mutate({ activeKey, page: 1 });
        break;
      case "3":
        !user_id ? message.info("请输入用户Id") : getPrintData.mutate({ activeKey, page: 1 });
        break;
      case "4":
        !mobile ? message.info("请输入手机号") : getPreData.mutate({ activeKey, page: 1 });
        break;
      case "5":
        !user_id ? message.info("请输入用户Id") : getInviteData.mutate({ activeKey, page: 1 });
        break;
      case "6":
        !user_id ? message.info("请输入用户Id") : getCommissionData.mutate({ activeKey, page: 1 });
    }
  });

  // 用户管理
  const getUserData = useMutation(async (param: any) => {
    // 获取数据
    const params = { id: userSearch?.user_id, username: userSearch?.mobile, page: param?.page };
    const res: any = await getUserList(params);
    setUserSearch({ ...userSearch, data: res, ...param });
  });

  // 订单管理
  const getOrderData = useMutation(async (param: any) => {
    // 获取数据
    const params = { user_id: userSearch?.user_id, page: param?.page };
    const res: any = await getOrderList(params);
    // 数据格式化
    const result = res?.list?.data.map((item: any) => ({
      ...item,
      pay_amount: (item?.pay_amount / 100).toFixed(2),
      total_amount: (item?.total_amount / 100).toFixed(2),
    }));
    setUserSearch({ ...userSearch, data: { ...res, list: { ...res?.list, data: result }}, ...param });
  });

  // 打印管理
  const getPrintData = useMutation(async (param: any) => {
    // 获取数据
    const params = { user_id: userSearch?.user_id, page: param?.page };
    const res: any = await getPrintBookRecordList(params);
    setUserSearch({ ...userSearch, data: res, ...param });
  });

  // 预购管理
  const getPreData = useMutation(async (param: any) => {
    // 获取数据
    const params = { mobile: userSearch?.mobile, page: param?.page };
    const res: any = await getPreLockList(params);
    setUserSearch({ ...userSearch, data: res, ...param });
  });

  // 邀请记录
  const getInviteData = useMutation(async (param: any) => {
    // 获取数据
    const params = { inviter_id: userSearch?.user_id, page: param?.page };
    const res: any = await getInviteRelationsList(params);
    setUserSearch({ ...userSearch, data: res, ...param });
  });

  // 佣金记录
  const getCommissionData = useMutation(async (param: any) => {
    // 获取数据
    const params = { user_id: userSearch?.user_id, page: param?.page };
    const res: any = await getCommissionRecordsList(params);
    // 数据格式化
    const result = res?.list?.data.map((item: any) => ({
      ...item,
      amount: (item?.amount / 100).toFixed(2),
      order_amount: (item?.order_amount / 100).toFixed(2),
      commission_rate: `${(item?.commission_rate / 100).toFixed(0)}%`,
    }));
    setUserSearch({ ...userSearch, data: { ...res, list: { ...res?.list, data: result }}, ...param });
  });

  // Tab配置
  const tabsConfig = [
    {
      label: "用户管理",
      key: "1",
      children: (
        <div className="">
          <Table
            // className="mt-3"
            // tableLayout="auto"
            columns={[
              { title: "Id", width: 160, dataIndex: "id", align: "center" },
              { title: "用户名", width: 150, dataIndex: "username", align: "center" },
              { title: "昵称", width: 200, dataIndex: "nickname", align: "center" },
              {
                title: "年级",
                width: 100,
                dataIndex: "grade",
                align: "center",
                render: (text: number) => (
                  <div className="">{_.find(GradeConfig, { value: text })?.label}</div>
                ),
              },
              {
                title: "状态",
                width: 100,
                dataIndex: "user_status",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(UserStatus, { value: text })?.color} className="mr-0">{_.find(UserStatus, { value: text })?.label}</Tag>
                ),
              },
              {
                title: "公网Ip",
                width: 150,
                dataIndex: "ip",
                align: "center",
                render: (text: any) => (
                  <div className="">{_.split(text, ',')[0]}</div>
                ),
              },
              {
                title: "密码",
                width: 120,
                dataIndex: "password",
                align: "center",
                render: (text: any) => (
                  <Tag color={text ? "success" : "error"} className="mr-0">{text ? "已设置" : "未设置"}</Tag>
                ),
              },
              {
                title: "渠道",
                width: 220,
                dataIndex: "qudao",
                align: "center",
                render: (text: number) => (
                  <div className="">{_.find(RegisterChannel, { value: text })?.label || text}</div>
                ),
              },
              {
                title: "注册时间",
                width: 180,
                dataIndex: "reg_time",
                align: "center",
                render: (text: any, record: any) => (
                  <div className="">{dayjs(new Date(text * 1000)).format("YYYY-MM-DD HH:mm:ss")}</div>
                )
              },
              { title: "最近登录时间", width: 180, dataIndex: "last_login_time", align: "center" },
            ]}
            dataSource={userSearch?.data?.list?.data}
            rowKey="id"
            pagination={false}
            size="small"
            loading={getUserData.isLoading}
            scroll={{ y: document.body.clientHeight - 200 }}
            title={() => (
              <div className="flex justify-between items-center w-full">
                <Pagination
                  current={userSearch?.page}
                  size="small"
                  total={userSearch?.data?.list?.total}
                  showTotal={(total) => `共 ${total} 条`}
                  pageSize={pageSize}
                  showQuickJumper
                  onChange={(page) => getUserData.mutate({ page, activeKey: userSearch?.activeKey })}
                  showSizeChanger={false}
                />
              </div>
            )}
          />
        </div>
      ),
    },
    {
      label: "订单管理",
      key: "2",
      children: (
        <div className="">
          <Table
            // className="mt-3"
            // tableLayout="auto"
            columns={[
              { title: "Id", width: 100, dataIndex: "id", align: "center" },
              { title: "订单编号", dataIndex: "order_no", width: 260, align: "center" },
              {
                title: "订单信息",
                width: 150,
                dataIndex: "order_items",
                align: "center",
                render: (text: any, record: any) => (
                  <div className="">
                    {text?.map((item: any) => item?.sku_spec_name).join("、")}
                  </div>
                ),
              },
              { title: "订单金额", width: 120, dataIndex: "total_amount", align: "center" },
              { title: "支付金额", width: 120, dataIndex: "pay_amount", align: "center" },
              {
                title: "订单类型",
                width: 120,
                dataIndex: "order_type",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(OrderType, { value: text })?.color} className="mr-0">{_.find(OrderType, { value: text })?.label}</Tag>
                ),
              },
              {
                title: "订单状态",
                width: 120,
                dataIndex: "status",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(OrderStatus, { value: text })?.color} className="mr-0">{_.find(OrderStatus, { value: text })?.label}</Tag>
                ),
              },
              { title: "创建时间", width: 180, dataIndex: "created_at", align: "center" },
            ]}
            dataSource={userSearch?.data?.list?.data}
            rowKey="id"
            pagination={false}
            size="small"
            loading={getOrderData.isLoading}
            scroll={{ y: document.body.clientHeight - 200 }}
            title={() => (
              <div className="flex justify-between items-center w-full">
                <Pagination
                  current={userSearch?.page}
                  size="small"
                  total={userSearch?.data?.list?.total}
                  showTotal={(total) => `共 ${total} 条`}
                  pageSize={pageSize}
                  showQuickJumper
                  onChange={(page) => getOrderData.mutate({ page, activeKey: userSearch?.activeKey })}
                  showSizeChanger={false}
                />
              </div>
            )}
          />
        </div>
      ),
    },
    {
      label: "打印管理",
      key: "3",
      children: (
        <div className="">
          <Table
            // className="mt-3"
            // tableLayout="auto"
            columns={ [
              { title: "Id", width: 100, dataIndex: "id", align: "center" },
              { title: "标题", dataIndex: "title", width: 320, align: "center" },
              // { title: "用户Id", width: 120, dataIndex: "user_id", align: "center" },
              { title: "总页数", dataIndex: "page_num", width: 80, align: "center" },
              {
                title: "打印类型",
                width: 120,
                dataIndex: "type",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(PrintType, { value: text })?.color} className="mr-0">{_.find(PrintType, { value: text })?.label}</Tag>
                ),
              },
              {
                title: "状态",
                width: 120,
                dataIndex: "status",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(PrintStatus, { value: text })?.color} className="mr-0">{_.find(PrintStatus, { value: text })?.label}</Tag>
                ),
              },
              { title: "创建时间", width: 180, dataIndex: "created_at", align: "center" },
              { title: "失败原因", dataIndex: "fail_msg", width: 180, align: "center" },
              {
                width: 100,
                title: "操作",
                dataIndex: "action",
                align: "center",
                render: (_text: any, record: any) => (
                  <>
                    <Button
                      type="link"
                      className="rounded"
                      disabled={record?.pdf_url === ''}
                      onClick={() => window.open(record?.pdf_url)}
                    >
                      下载
                    </Button>
                  </>
                ),
              },
            ]}
            dataSource={userSearch?.data?.list?.data}
            rowKey="id"
            pagination={false}
            size="small"
            loading={getPrintData.isLoading}
            scroll={{ y: document.body.clientHeight - 200 }}
            title={() => (
              <div className="flex justify-between items-center w-full">
                <Pagination
                  current={userSearch?.page}
                  size="small"
                  total={userSearch?.data?.list?.total}
                  showTotal={(total) => `共 ${total} 条`}
                  pageSize={pageSize}
                  showQuickJumper
                  onChange={(page) => getPrintData.mutate({ page, activeKey: userSearch?.activeKey })}
                  showSizeChanger={false}
                />
              </div>
            )}
          />
        </div>
      )
    },
    {
      label: "预购管理",
      key: "4",
      children: (
        <div className="">
          <Table
            // className="mt-3"
            // tableLayout="auto"
            columns={ [
              { title: "Id", dataIndex: "id", width: 100, align: "center" },
              // { title: "手机号", dataIndex: "mobile", width: 180, align: "center" },
              {
                title: "权益类型",
                width: 120,
                dataIndex: "name",
                align: "center",
                render: (text: any, record: any) => (
                  <div className="">{_.find(rightsDict, { rights_sub_type: record?.rights_sub_type })?.name}</div>
                ),
              },
              { title: "单位", width: 100, dataIndex: "unit", align: "center" },
              { title: "值", width: 100, dataIndex: "amount", align: "center" },
              {
                title: "状态",
                dataIndex: "status",
                width: 180,
                align: "center",
                render: (text: any, record: any) => (
                  <Tag color={_.find(PreLockStatus, { value: text })?.color} className="mr-0">
                    {_.find(PreLockStatus, { value: text })?.label}
                  </Tag>
                ),
              },
              { title: "创建时间", width: 180, dataIndex: "created_at", align: "center" },
              { title: "失败原因", dataIndex: "fail_msg", width: 180, align: "center" },
            ]}
            dataSource={userSearch?.data?.list?.data}
            rowKey="id"
            pagination={false}
            size="small"
            loading={getPrintData.isLoading}
            scroll={{ y: document.body.clientHeight - 200 }}
            title={() => (
              <div className="flex justify-between items-center w-full">
                <Pagination
                  current={userSearch?.page}
                  size="small"
                  total={userSearch?.data?.list?.total}
                  showTotal={(total) => `共 ${total} 条`}
                  pageSize={pageSize}
                  showQuickJumper
                  onChange={(page) => getPreData.mutate({ page, activeKey: userSearch?.activeKey })}
                  showSizeChanger={false}
                />
              </div>
            )}
          />
        </div>
      )
    },
    {
      label: "邀请记录",
      key: "5",
      children: (
        <div className="">
          <Table
            // className="mt-3"
            // tableLayout="auto"
            columns={ [
              { title: "Id", dataIndex: "id", width: 100, align: "center" },
              { title: "用户Id", width: 120, dataIndex: "user_id", align: "center" },
              {
                title: "用户名",
                width: 160,
                dataIndex: "username",
                align: "center",
                render: (text: any, record: any) => (
                  <div className="">{record?.user?.username}</div>
                ),
              },
              {
                title: "昵称",
                width: 160,
                dataIndex: "nickname",
                align: "center",
                render: (text: any, record: any) => (
                  <div className="">{record?.user?.nickname}</div>
                ),
              },
              {
                title: "间接邀请人Id",
                width: 120,
                dataIndex: "root_inviter_id",
                align: "center",
                render: (text: number) => (
                  <div className="">{text || ""}</div>
                ),
              },
              {
                title: "绑定方式",
                width: 120,
                dataIndex: "bind_type",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(BindType, { value: text })?.color} className="mr-0">{_.find(BindType, { value: text })?.label}</Tag>
                ),
              },
            ]}
            dataSource={userSearch?.data?.list?.data}
            rowKey="id"
            pagination={false}
            size="small"
            loading={getPrintData.isLoading}
            scroll={{ y: document.body.clientHeight - 200 }}
            title={() => (
              <div className="flex justify-between items-center w-full">
                <Pagination
                  current={userSearch?.page}
                  size="small"
                  total={userSearch?.data?.list?.total}
                  showTotal={(total) => `共 ${total} 条`}
                  pageSize={pageSize}
                  showQuickJumper
                  onChange={(page) => getInviteData.mutate({ page, activeKey: userSearch?.activeKey })}
                  showSizeChanger={false}
                />
              </div>
            )}
          />
        </div>
      )
    },
    {
      label: "佣金记录",
      key: "6",
      children: (
        <div className="">
          <Table
            // className="mt-3"
            // tableLayout="auto"
            columns={ [
              { title: "Id", dataIndex: "id", width: 100, align: "center" },
              { title: "订单编号", dataIndex: "order_no", width: 260, align: "center" },
              { title: "订单金额", dataIndex: "order_amount", width: 120, align: "center" },
              { title: "佣金", dataIndex: "amount", width: 120, align: "center" },
              {
                title: "状态",
                width: 120,
                dataIndex: "status",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(CommissionStatus, { value: text })?.color} className="mr-0">{_.find(CommissionStatus, { value: text })?.label}</Tag>
                ),
              },
              { title: "结算时间", width: 180, dataIndex: "settle_time", align: "center" },
              {
                title: "消费用户Id",
                width: 120,
                dataIndex: "source_user_id",
                align: "center",
                render: (text: number) => (
                  <div className="">{text || ""}</div>
                ),
              },
              {
                title: "推荐层级",
                width: 120,
                dataIndex: "level",
                align: "center",
                render: (text: number) => (
                  <Tag color={_.find(InviteLevel, { value: text })?.color} className="mr-0">{_.find(InviteLevel, { value: text })?.label}</Tag>
                ),
              },
              { title: "分佣比例", dataIndex: "commission_rate", width: 120, align: "center" },
              { title: "创建时间", width: 180, dataIndex: "created_at", align: "center" },
              { title: "备注", dataIndex: "remark", width: 320, align: "center" },
            ]}
            dataSource={userSearch?.data?.list?.data}
            rowKey="id"
            pagination={false}
            size="small"
            loading={getPrintData.isLoading}
            scroll={{ y: document.body.clientHeight - 200 }}
            title={() => (
              <div className="flex justify-between items-center w-full">
                <Pagination
                  current={userSearch?.page}
                  size="small"
                  total={userSearch?.data?.list?.total}
                  showTotal={(total) => `共 ${total} 条`}
                  pageSize={pageSize}
                  showQuickJumper
                  onChange={(page) => getCommissionData.mutate({ page, activeKey: userSearch?.activeKey })}
                  showSizeChanger={false}
                />
              </div>
            )}
          />
        </div>
      )
    }
  ];

  // 处理初始化
  const handleInit = useMutation(async () => {
    // 解构
    const { user_id, mobile, activeKey } = userSearch;
    (user_id || mobile) && handleSearch.mutate(activeKey);
  });

  // 监听
  useEffect(() => {
    userSearch?.visible && handleInit.mutate();
  }, [userSearch?.visible]);

  return (
    <div className="">
      <div onClick={() => setUserSearch({ visible: true, user_id: '', mobile: '', activeKey: '1', data: [] })} className="text-[#FF9900] font-semibold flex items-center cursor-pointer">
        <SearchOutlined className="text-[18px]" />
        <div className="ml-0.5">搜索</div>
      </div>
      <Modal width="80vw" title="搜索" footer={null} open={userSearch?.visible} onCancel={() => setUserSearch({ ...userSearch, visible: false })} style={{ minWidth: '720px' }}>
        <div className="mt-3">
          <div className="flex items-center">
            <div className="flex items-center">
              {!_.includes(["4"], userSearch?.activeKey) && <Input placeholder="用户Id" value={userSearch?.user_id} onInput={(e: any) => setUserSearch({ ...userSearch, user_id: e.target.value, mobile: '', data: null, page: 1 })} allowClear onClear={() => setUserSearch({ ...userSearch, user_id: '', data: null, page: 1 })} className="" />}
              {_.includes(["1", "4"], userSearch?.activeKey) && <Input placeholder="手机号" value={userSearch?.mobile} onInput={(e: any) => setUserSearch({ ...userSearch, mobile: e.target.value, user_id: '', data: null, page: 1 })} allowClear onClear={() => setUserSearch({ ...userSearch, mobile: '', data: null, page: 1 })} className="ml-3" />}
            </div>
            <Button type="primary" className="ml-5 rounded" loading={handleSearch.isLoading || getUserData.isLoading || getOrderData.isLoading || getPrintData.isLoading || getPreData.isLoading || getInviteData.isLoading || getCommissionData.isLoading} onClick={() => handleSearch.mutate(userSearch?.activeKey)}>
              搜索
            </Button>
            <Button className="rounded ml-2" onClick={() => setUserSearch({ ...userSearch, user_id: '', mobile: '', activeKey: '1', data: null, page: 1 })}>
              重置
            </Button>
          </div>
          <div className="mt-2">
            <Tabs items={tabsConfig} activeKey={userSearch?.activeKey} onChange={(key) => handleSearch.mutate(key)}>
            </Tabs>
          </div>
        </div>
      </Modal>
    </div>
  );
}
